import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import withRetry from '../utils/withRetry';
import { getSessionStorageSafe } from '../utils/sessionStorage';

const useAdsStore = create(
  persist(
    (set, get) => ({
      ads: [],
      selectedAd: null,
      loading: false,
      error: null,
      isAdPreviewModalOpen: false,
      adToPreview: null,
      adToEdit: null,
      editingAdForm: null,
      isSavingAd: false,
      adEditError: null,

      fetchAllAds: async () => {
        try {
          set({ loading: true, error: null });
          const response = await withRetry(() => apiClient.get(API_ENDPOINTS.getAllAds));
          set({ ads: response.data, loading: false });
        } catch (error) {
          console.error("Failed to fetch ads", error);
          set({ error: 'Failed to fetch ads.', loading: false });
        }
      },

      fetchAdById: async (adId) => {
        try {
          set({ loading: true, error: null, selectedAd: null });

          const existing = get().ads.find(ad => ad.id === Number(adId) || ad.id === adId);
          if (existing) {
            set({ selectedAd: existing, loading: false });
            return existing;
          }

          // Fallback: fetch all ads then search
          await get().fetchAllAds();
          const found = get().ads.find(ad => ad.id === Number(adId) || ad.id === adId);
          if (found) {
            set({ selectedAd: found, loading: false });
            return found;
          }

          set({ error: 'Ad not found.', loading: false });
          return null;
        } catch (error) {
          console.error('Failed to fetch ad by id', error);
          set({ error: 'Failed to fetch ad.', loading: false });
          throw error;
        }
      },

      // Actions to control the modal
      openAdPreview: (ad) => set({ isAdPreviewModalOpen: true, adToPreview: ad }),
      closeAdPreview: () => set({ isAdPreviewModalOpen: false, adToPreview: null }),
      setAdToEdit: (ad) => {
        set({ adToEdit: ad, editingAdForm: ad ? { ...ad, startDate: ad.startDate ? new Date(ad.startDate).toISOString().slice(0, 16) : '', endDate: ad.endDate ? new Date(ad.endDate).toISOString().slice(0, 16) : '' } : null });
      },
      updateEditingAdField: (field, value) => set((state) => ({
        editingAdForm: state.editingAdForm ? { ...state.editingAdForm, [field]: value } : null,
      })),
      startSavingAd: () => set({ isSavingAd: true, adEditError: null }),
      stopSavingAd: (error = null) => set({ isSavingAd: false, adEditError: error }),
      resetEditingAdForm: () => set({ editingAdForm: null, isSavingAd: false, adEditError: null }),
    }),
    {
      name: 'ads-store',
      storage: createJSONStorage(getSessionStorageSafe),
      partialize: (state) => ({
        ads: state.ads,
        selectedAd: state.selectedAd,
        adToEdit: state.adToEdit,
        editingAdForm: state.editingAdForm,
      }),
    }
  )
);

export default useAdsStore;
